// src/utils/availabilityImageSettingsUtil.js
//
// Reading, validating and remembering the Availability images settings
// (font sizes in px, BOOKED cell colours). They are kept in this browser's
// localStorage only: they shape a picture the admin is about to post, not
// anything guests see, so they don't belong in the club config.

import {
  AVAILABILITY_IMAGE_DEFAULT_SETTINGS,
  AVAILABILITY_IMAGE_SETTINGS_STORAGE_KEY,
  AVAILABILITY_IMAGE_TEXT_PX_MAX,
  AVAILABILITY_IMAGE_TEXT_PX_MIN,
} from '../constants/bookingConst';

const HEX_COLOR = /^#[0-9a-f]{6}$/i;

/**
 * A px value as stored: a number inside the allowed range, or null (auto).
 * @param {unknown} value
 * @returns {number|null}
 */
const toPx = (value) => {
  const px = Number(value);
  if (value === null || value === '' || !Number.isFinite(px)) return null;
  if (px < AVAILABILITY_IMAGE_TEXT_PX_MIN || px > AVAILABILITY_IMAGE_TEXT_PX_MAX) return null;
  return Math.round(px * 10) / 10;
};

/**
 * A colour as stored: a 6-digit hex, or the fallback.
 * @param {unknown} value
 * @param {string} fallback
 * @returns {string}
 */
const toColor = (value, fallback) => (typeof value === 'string' && HEX_COLOR.test(value) ? value.toLowerCase() : fallback);

/**
 * Cleans a settings object so every field is valid; anything wrong falls
 * back to its default.
 * @param {object} [raw]
 * @returns {{timePx: number|null, cellPx: number|null, bookedBg: string, bookedText: string}}
 */
export const normalizeImageSettings = (raw = {}) => ({
  timePx: toPx(raw?.timePx),
  cellPx: toPx(raw?.cellPx),
  bookedBg: toColor(raw?.bookedBg, AVAILABILITY_IMAGE_DEFAULT_SETTINGS.bookedBg),
  bookedText: toColor(raw?.bookedText, AVAILABILITY_IMAGE_DEFAULT_SETTINGS.bookedText),
});

/**
 * The settings this browser last saved, or the defaults. Never throws:
 * storage can be blocked (private mode) or hold junk.
 * @returns {object} normalized settings
 */
export const loadImageSettings = () => {
  try {
    const stored = window.localStorage.getItem(AVAILABILITY_IMAGE_SETTINGS_STORAGE_KEY);
    return normalizeImageSettings(stored ? JSON.parse(stored) : AVAILABILITY_IMAGE_DEFAULT_SETTINGS);
  } catch {
    return normalizeImageSettings(AVAILABILITY_IMAGE_DEFAULT_SETTINGS);
  }
};

/**
 * Remembers the settings in this browser. A failed write is ignored — the
 * settings still apply for this session.
 * @param {object} settings - normalized settings
 * @returns {void}
 */
export const saveImageSettings = (settings) => {
  try {
    window.localStorage.setItem(AVAILABILITY_IMAGE_SETTINGS_STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // Storage unavailable; nothing to do.
  }
};

/**
 * Validates the settings form. Font-size fields may be blank (automatic) or
 * a number of px inside the allowed range.
 * @param {{timePx: string, cellPx: string, bookedBg: string, bookedText: string}} draft - raw form values
 * @returns {{ok: boolean, settings: object|null, errors: {[field: string]: string}}}
 */
export const validateImageSettings = (draft) => {
  const errors = {};
  ['timePx', 'cellPx'].forEach((field) => {
    const raw = String(draft[field] ?? '').trim();
    if (raw === '') return;
    const px = Number(raw);
    if (!Number.isFinite(px) || px < AVAILABILITY_IMAGE_TEXT_PX_MIN || px > AVAILABILITY_IMAGE_TEXT_PX_MAX) {
      errors[field] = `Enter ${AVAILABILITY_IMAGE_TEXT_PX_MIN}–${AVAILABILITY_IMAGE_TEXT_PX_MAX} px, or leave blank for automatic.`;
    }
  });
  ['bookedBg', 'bookedText'].forEach((field) => {
    if (!HEX_COLOR.test(String(draft[field] || ''))) errors[field] = 'Pick a colour.';
  });
  if (Object.keys(errors).length > 0) return { ok: false, settings: null, errors };
  return {
    ok: true,
    errors,
    settings: normalizeImageSettings({
      ...draft,
      timePx: String(draft.timePx ?? '').trim() === '' ? null : draft.timePx,
      cellPx: String(draft.cellPx ?? '').trim() === '' ? null : draft.cellPx,
    }),
  };
};
